import { useRecentSearches } from "@/hooks/useRecentSearches";
import { analytics } from "@/lib/analytics";
import type { TypesenseProduct } from "@/lib/typesense";
import type { SearchType } from "@/pages/search/models";
import { useCategoryProductCounts } from "@/queries/categories";
import {
  PREVIOUS_ORDERS_WINDOW_DAYS,
  usePreviousOrderProductIds
} from "@/queries/orders/previous-product-ids";
import { formatPrice } from "@/utils/money";
import { getProductDisplayTitle } from "@/utils/product";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  Clock,
  History,
  Layers,
  Package,
  Search,
  Store,
  Tag
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useHits, useSearchBox } from "react-instantsearch";
import {
  getSelectableActions,
  type Action,
  type SearchNav
} from "./search-suggestions";

function useDebounce<T>(value: T, delay: number = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Id of the "Previous orders" quick action. A constant rather than a string
 * literal at each site because both the ranking and the analytics branch key
 * off it.
 */
const PREVIOUS_ORDERS_ACTION_ID = "previous-orders";

/** Shown in the desktop input and echoed by the phone overlay's trigger. */
export const SEARCH_PLACEHOLDER = "Search for products, brands, or SKU...";

interface SearchResult {
  actions: Action[];
}

/**
 * The price line on a product suggestion. Products are sold by several
 * suppliers at different prices, so a bare figure would read as *the* price
 * when it is only the cheapest one - `from` says which.
 */
function formatSuggestionPrice(minPrice: number, maxPrice: number): string {
  const min = formatPrice(minPrice);
  return maxPrice > minPrice ? `from ${min}` : min;
}

// Limits for each result type
const LIMITS = {
  products: 4,
  brands: 2,
  categories: 2,
  suppliers: 2
};

/**
 * Transform Typesense product results into grouped actions with limits
 */
function transformProductsToActions(products: TypesenseProduct[]): Action[] {
  const productActions: Action[] = [];
  const brandActions: Action[] = [];
  const categoryActions: Action[] = [];
  const supplierActions: Action[] = [];

  const seenBrands = new Set<string>();
  const seenCategories = new Set<string>();
  const seenSellers = new Set<string>();

  for (const product of products) {
    // Add product (up to limit)
    if (productActions.length < LIMITS.products) {
      productActions.push({
        id: `product-${product.id}`,
        label: getProductDisplayTitle({
          title: product.title,
          brand: product.brand.name
        }),
        // Still the fallback when a product has no image, or its image 404s.
        icon: <Package className="h-4 w-4 text-purple-500" />,
        end: "Product",
        // No `description`: the category list used to be the row's second
        // line, and price + supplier count is a straighter answer to "is this
        // the one I want" than "Hair Colour, Colour" - which the group header
        // and the results page both already say. Two lines is the budget at
        // 360px with a thumbnail in front of them.
        product: {
          thumbnail: product.thumbnail,
          price: formatSuggestionPrice(
            product.meta.min_price,
            product.meta.max_price
          ),
          supplierCount: product.sellers.length
        }
      });
    }

    // Add unique brands (up to limit)
    if (
      product.brand.name &&
      !seenBrands.has(product.brand.id) &&
      brandActions.length < LIMITS.brands
    ) {
      seenBrands.add(product.brand.id);
      brandActions.push({
        id: `brand-${product.brand.id}`,
        label: product.brand.name,
        icon: <Tag className="h-4 w-4 text-pink-500" />,
        end: "Brand"
      });
    }

    // Add unique categories (up to limit)
    for (const category of product.categories) {
      if (
        category.name &&
        !seenCategories.has(category.id) &&
        categoryActions.length < LIMITS.categories
      ) {
        seenCategories.add(category.id);
        categoryActions.push({
          id: `category-${category.id}`,
          label: category.name,
          icon: <Search className="h-4 w-4 text-blue-500" />,
          end: "Category"
        });
      }
    }

    // Add unique suppliers (up to limit)
    for (const seller of product.sellers) {
      if (
        seller.name &&
        !seenSellers.has(seller.id) &&
        supplierActions.length < LIMITS.suppliers
      ) {
        seenSellers.add(seller.id);
        supplierActions.push({
          id: `seller-${seller.id}`,
          label: seller.name,
          icon: <Store className="h-4 w-4 text-green-500" />,
          end: "Supplier"
        });
      }
    }
  }

  // Build grouped results with headers
  const actions: Action[] = [];

  if (productActions.length > 0) {
    actions.push({
      id: "header-products",
      label: "Products",
      icon: <Package className="h-3 w-3" />,
      isHeader: true
    });
    actions.push(...productActions);
  }

  if (brandActions.length > 0) {
    actions.push({
      id: "header-brands",
      label: "Brands",
      icon: <Tag className="h-3 w-3" />,
      isHeader: true
    });
    actions.push(...brandActions);
  }

  if (categoryActions.length > 0) {
    actions.push({
      id: "header-categories",
      label: "Categories",
      icon: <Search className="h-3 w-3" />,
      isHeader: true
    });
    actions.push(...categoryActions);
  }

  if (supplierActions.length > 0) {
    actions.push({
      id: "header-suppliers",
      label: "Suppliers",
      icon: <Store className="h-3 w-3" />,
      isHeader: true
    });
    actions.push(...supplierActions);
  }

  return actions;
}

interface CategoryCount {
  name: string;
  handle: string;
  product_count: number;
}

// How many default quick actions to show per group
const DEFAULT_LIMITS = {
  recent: 5,
  categoriesWithRecent: 4,
  categoriesOnly: 6
};

/**
 * Build the quick actions shown when the search box is focused but empty:
 * the salon's own previously-ordered products, then their recent searches,
 * then the most popular categories. Acts as a "never empty" default so users
 * always have a shortcut to start from.
 *
 * The tiers are ordered strongest-signal-first (#8's Tier 1 before the MVP's
 * Tier 3): what this salon actually buys beats what they last typed, which
 * beats what the catalogue is largest in.
 *
 * @param previousOrderProductCount how many distinct products the salon
 * ordered in the trailing window. Zero omits the tier entirely, which is the
 * same branch for a guest (the endpoint 401s -> no data) and for a customer
 * with no qualifying orders - neither should be offered a dead-end shortcut.
 */
function buildDefaultActions(
  recentSearches: string[],
  categories: CategoryCount[],
  previousOrderProductCount: number
): Action[] {
  const actions: Action[] = [];

  // No header: a one-row section whose header repeated its only label reads as
  // a mistake. Rendered bare at the top, it reads as the top hit it is.
  if (previousOrderProductCount > 0) {
    actions.push({
      id: PREVIOUS_ORDERS_ACTION_ID,
      label: "Previous orders",
      icon: <History className="h-4 w-4 text-amber-500" />,
      description: `${previousOrderProductCount} ${
        previousOrderProductCount === 1 ? "product" : "products"
      } from the last ${PREVIOUS_ORDERS_WINDOW_DAYS} days`,
      end: "Your orders",
      navSearch: { previous_orders: true }
    });
  }

  const recent = recentSearches.slice(0, DEFAULT_LIMITS.recent);
  if (recent.length > 0) {
    actions.push({
      id: "header-recent",
      label: "Recent searches",
      icon: <Clock className="h-3 w-3" />,
      isHeader: true
    });
    for (const term of recent) {
      actions.push({
        id: `recent-${term}`,
        label: term,
        icon: <Clock className="h-4 w-4 text-gray-400" />,
        end: "Recent",
        navSearch: { q: term }
      });
    }
  }

  const topCategories = [...categories]
    .sort((a, b) => b.product_count - a.product_count)
    .slice(
      0,
      recent.length > 0
        ? DEFAULT_LIMITS.categoriesWithRecent
        : DEFAULT_LIMITS.categoriesOnly
    );

  if (topCategories.length > 0) {
    actions.push({
      id: "header-popular-categories",
      label: "Popular categories",
      icon: <Layers className="h-3 w-3" />,
      isHeader: true
    });
    for (const category of topCategories) {
      actions.push({
        id: `category-${category.handle}`,
        label: category.name,
        icon: <Search className="h-4 w-4 text-blue-500" />,
        description: `${category.product_count}+ products`,
        end: "Category",
        navSearch: { q: category.name, type: "category" }
      });
    }
  }

  return actions;
}

export interface UseSearchSuggestionsOptions {
  /**
   * Whether the surface showing these suggestions is open.
   *
   * It gates three things: whether results are computed at all, whether the
   * input re-syncs from the URL, and whether the two lazy queries behind the
   * default actions (categories, previous-order ids) are enabled - so a page
   * with a closed search box costs nothing. The dropdown passes its focus
   * state; the full-screen search route passes `true`, because being on it
   * *is* the open state.
   */
  active: boolean;
  /**
   * Called after a selection has navigated to `/search`. The two surfaces want
   * different things here - the dropdown closes itself, the route leaves - so
   * neither is baked in.
   */
  onNavigated?: () => void;
  /**
   * Replace the current history entry when navigating to `/search` instead of
   * pushing a new one. True for the search *route*, which is an entry of its
   * own: pushing would put it between the results and wherever search was
   * opened from, so Back from the results would land on the search page again,
   * refocus the field and raise the keyboard. The dropdown occupies no entry
   * and so has nothing to replace.
   */
  replace?: boolean;
}

export interface SearchSuggestionsController {
  inputValue: string;
  setInputValue: (value: string) => void;
  /** The grouped rows to render, or `null` when there is nothing to show. */
  actions: Action[] | null;
  /** The subset a keystroke can land on - see `getSelectableActions`. */
  selectableActions: Action[];
  highlightedIndex: number;
  setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>;
  selectAction: (action: Action) => void;
  submitFreeTextSearch: (term: string) => void;
  handleKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Clear the input and the Typesense query without navigating. */
  clearQuery: () => void;
}

/**
 * Everything the search box does apart from drawing itself: the debounced
 * query, the grouped suggestions (live hits, or the never-empty default set),
 * keyboard navigation, the analytics, and the navigation to `/search`.
 *
 * Extracted because there are two surfaces now and only one behaviour. The
 * header dropdown and the phone search route differ in *shape* - one is
 * anchored under a `max-w-xl` bar, the other is a page - and in nothing else.
 * Duplicating this would mean two implementations of "what does Enter do",
 * which is exactly the drift `SearchSuggestionList` was extracted to stop for
 * the rows. See salonstocker/storefront#158.
 */
export function useSearchSuggestions({
  active,
  onNavigated,
  replace = false
}: UseSearchSuggestionsOptions): SearchSuggestionsController {
  const [result, setResult] = useState<SearchResult | null>(null);
  const [selectedAction, setSelectedAction] = useState<Action | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const navigate = useNavigate();

  // Always read, so the input stays in step with the URL's `q`.
  const searchParams = useSearch({ strict: false });
  const urlQuery = (searchParams as { q?: string }).q;

  const { query, refine } = useSearchBox();
  const { hits } = useHits<TypesenseProduct>();

  const { recentSearches, addRecentSearch } = useRecentSearches();
  const { data: categoryData } = useCategoryProductCounts({ enabled: active });
  const categories = useMemo<CategoryCount[]>(
    () => categoryData?.categories ?? [],
    [categoryData]
  );
  // Same lazy contract as categories, so opening any page costs nothing. A
  // failure (a guest's 401) yields an empty list, which collapses to the same
  // "no tier" branch - omitting the action is the right fallback here, unlike
  // on the results page where it would be a claim about their history.
  const { productIds: previousOrderProductIds } = usePreviousOrderProductIds({
    enabled: active
  });

  const [inputValue, setInputValue] = useState("");
  const debouncedInputValue = useDebounce(inputValue, 200);

  useEffect(() => {
    if (!active) {
      setInputValue(urlQuery ?? "");
    }
  }, [urlQuery, active]);

  // Sync the debounced input with Typesense (minimum 2 characters).
  useEffect(() => {
    if (debouncedInputValue.trim().length >= 2) {
      refine(debouncedInputValue);
    } else if (query !== "") {
      refine("");
    }
  }, [debouncedInputValue, refine, query]);

  useEffect(() => {
    if (!active) {
      setResult(null);
      return;
    }

    // Open but the query is too short: the never-empty default set (recent
    // searches, popular categories, previous orders) rather than nothing.
    if (!query.trim() || query.trim().length < 2) {
      const defaultActions = buildDefaultActions(
        recentSearches,
        categories,
        previousOrderProductIds.length
      );
      setResult(defaultActions.length > 0 ? { actions: defaultActions } : null);
      return;
    }

    setResult({ actions: transformProductsToActions(hits.slice(0, 10)) });
  }, [
    hits,
    active,
    query,
    recentSearches,
    categories,
    previousOrderProductIds
  ]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [result]);

  // A selection has been made and navigation is under way; keep the rows down
  // so the surface does not flash the old list on its way out.
  const actions = result && !selectedAction ? result.actions : null;
  const selectableActions = result ? getSelectableActions(result.actions) : [];

  const clearQuery = () => {
    setInputValue("");
    refine("");
  };

  const navigateToSearch = (search: SearchNav) => {
    navigate({ to: "/search", search, replace });
    clearQuery();
    onNavigated?.();
  };

  const selectAction = (action: Action) => {
    setSelectedAction(action);
    const search: SearchNav = action.navSearch ?? {
      q: action.label,
      type: action.end?.toLowerCase() as SearchType
    };

    // "Previous orders" carries no query, so the search-funnel events below
    // have nothing to attribute and would report a literal "Previous orders"
    // as a searched term. It reports itself instead - a distinct event, so
    // adoption of the first personalized quick action is measurable on its own
    // rather than blended into generic search volume (KAN-51).
    if (search.previous_orders) {
      analytics.previousOrdersOpened({
        product_count: previousOrderProductIds.length,
        window_days: PREVIOUS_ORDERS_WINDOW_DAYS
      });
      navigateToSearch(search);
      return;
    }

    // Remember plain free-text selections (e.g. re-picking a recent search).
    if (search.q && !search.type) {
      addRecentSearch(search.q);
    }
    analytics.searchSubmitted({
      query: search.q ?? action.label,
      trigger: "suggestion"
    });
    // Record the specific result clicked - but only real result types
    // (product/brand/category/supplier), not recent-search re-runs which have
    // no type. Position is the item's 0-based rank in the list.
    if (
      search.type === "product" ||
      search.type === "brand" ||
      search.type === "category" ||
      search.type === "supplier"
    ) {
      analytics.searchResultClicked({
        query,
        result_type: search.type,
        position: selectableActions.findIndex((a) => a.id === action.id)
      });
    }
    navigateToSearch(search);
  };

  const submitFreeTextSearch = (term: string) => {
    const q = term.trim();
    if (!q) return;
    addRecentSearch(q);
    analytics.searchSubmitted({ query: q, trigger: "typed" });
    navigateToSearch({ q });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!result || !selectableActions.length) {
      if (event.key === "Enter" && inputValue.trim()) {
        event.preventDefault();
        submitFreeTextSearch(inputValue);
      }
      return;
    }

    const maxIndex = selectableActions.length - 1;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((prev) => (prev < maxIndex ? prev + 1 : prev));
        break;

      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((prev) => (prev > -1 ? prev - 1 : -1));
        break;

      case "Enter":
        event.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex <= maxIndex) {
          selectAction(selectableActions[highlightedIndex]);
        } else if (inputValue.trim()) {
          submitFreeTextSearch(inputValue);
        }
        break;

      default:
        break;
    }
  };

  // Focusing again after a selection has to show rows once more; the surfaces
  // call this through `active` flipping back on.
  useEffect(() => {
    if (active) {
      setSelectedAction(null);
    }
  }, [active]);

  return {
    inputValue,
    setInputValue,
    actions,
    selectableActions,
    highlightedIndex,
    setHighlightedIndex,
    selectAction,
    submitFreeTextSearch,
    handleKeyDown,
    clearQuery
  };
}
